import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, AccountBalanceOutlined, CheckCircleOutline,
    LockOutlined, ContentCopyOutlined, CheckOutlined,
    ShieldOutlined, VerifiedOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
};

interface Bank {
    value: string;
    label: string;
    short: string;
    color: string;
}

const BANKS: Bank[] = [
    { value: 'access',     label: 'Access Bank',                   short: 'AB',  color: '#E31E24' },
    { value: 'zenith',     label: 'Zenith Bank',                   short: 'ZB',  color: '#CC0000' },
    { value: 'gtbank',     label: 'GTBank',                        short: 'GT',  color: '#F06400' },
    { value: 'first-bank', label: 'First Bank of Nigeria',         short: 'FB',  color: '#003087' },
    { value: 'uba',        label: 'UBA',                           short: 'UBA', color: '#E31837' },
    { value: 'kuda',       label: 'Kuda Bank',                     short: 'KD',  color: '#4E0CDB' },
    { value: 'opay',       label: 'Opay',                          short: 'OP',  color: '#00B050' },
    { value: 'palmpay',    label: 'PalmPay',                       short: 'PP',  color: '#00AC47' },
    { value: 'stanbic',    label: 'Stanbic IBTC',                  short: 'SI',  color: '#1E3A5F' },
    { value: 'sterling',   label: 'Sterling Bank',                 short: 'ST',  color: '#CC0000' },
    { value: 'wema',       label: 'Wema Bank',                     short: 'WB',  color: '#8B0000' },
    { value: 'fidelity',   label: 'Fidelity Bank',                 short: 'FD',  color: '#006B3F' },
];

// Already linked banks (mock)
const LINKED_BANKS = [
    { name: 'Access Bank', acct: '··· 7821', short: 'AB', color: '#E31E24' },
    { name: 'GTBank',      acct: '··· 4419', short: 'GT', color: '#F06400' },
];

const fmt = (s: string) => s.replace(/(\d{4})(?=\d)/g, '$1 ');

const LinkBankPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [step,        setStep]        = useState<1 | 2 | 3>(1);
    const [bank,        setBank]        = useState('');
    const [accountNo,   setAccountNo]   = useState('');
    const [acctTouched, setAcctTouched] = useState(false);
    const [loading,     setLoading]     = useState(false);
    const [done,        setDone]        = useState(false);
    const [copied,      setCopied]      = useState(false);

    const selectedBank = BANKS.find(b => b.value === bank);

    // Nigerian bank account numbers are exactly 10 digits (NUBAN standard)
    const acctClean  = accountNo.replace(/\s+/g, '');
    const acctValid  = /^\d{10}$/.test(acctClean);
    const acctError  = acctTouched && accountNo.length > 0 && !acctValid
        ? acctClean.length < 10
            ? 'Account number must be exactly 10 digits'
            : acctClean.length > 10
                ? 'Account number too long — must be 10 digits'
                : /[^0-9]/.test(acctClean)
                    ? 'Numbers only — no letters or symbols'
                    : ''
        : '';

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleVerify = () => {
        setLoading(true);
        setTimeout(() => { setLoading(false); setStep(2); }, 1800);
    };

    const handleLink = () => {
        setLoading(true);
        setTimeout(() => { setLoading(false); setDone(true); }, 1800);
    };

    const goBack = () => {
        if (step === 1) navigate('/wallet');
        else setStep(s => (s - 1) as any);
    };

    const handleCopyAcct = () => {
        navigator.clipboard.writeText(acctClean);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    /* ── SUCCESS ── */
    if (done) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center', maxWidth: 340, mx: 'auto' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Account Linked!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>Your bank account has been verified and linked</Typography>

                {/* Linked account chip */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, background: isDark ? alpha(B.mid, .8) : '#fff', border: surfaceBdr, borderRadius: '14px', px: 2, py: 1.25, mb: 4, mt: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '10px', background: alpha(selectedBank?.color ?? B.purple, .15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: selectedBank?.color ?? B.purple, fontWeight: 800, fontSize: '.7rem' }}>{selectedBank?.short}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.82rem' }}>{selectedBank?.label}</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>··· {acctClean.slice(-4)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .4, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 20, px: 1, py: .3 }}>
                        <VerifiedOutlined sx={{ color: B.green, fontSize: 11 }} />
                        <Typography sx={{ color: B.green, fontSize: '.6rem', fontWeight: 700 }}>LINKED</Typography>
                    </Box>
                </Box>

                <Box display="flex" gap={2} justifyContent="center">
                    <Button onClick={() => { setDone(false); setStep(1); setBank(''); setAccountNo(''); setAcctTouched(false); }}
                            sx={{ border: `1px solid ${alpha(isDark ? '#fff' : B.dark, .12)}`, color: textMid, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 600, fontSize: '.85rem' }}>
                        Link Another
                    </Button>
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 700, fontSize: '.85rem', boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Done
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.green, .07)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            Step {step} of 2
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Link Bank Account
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <ShieldOutlined sx={{ color: B.green, fontSize: 12 }} />
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>SECURE</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Already linked ── */}
                {LINKED_BANKS.length > 0 && step === 1 && (
                    <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                        <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Already Linked</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {LINKED_BANKS.map((b, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '12px', background: alpha(isDark ? '#fff' : B.dark, .03), border: `1px solid ${dividerC}` }}>
                                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: alpha(b.color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Typography sx={{ color: b.color, fontWeight: 800, fontSize: '.72rem' }}>{b.short}</Typography>
                                    </Box>
                                    <Box flex={1}>
                                        <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.82rem' }}>{b.name}</Typography>
                                        <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{b.acct}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .4, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 20, px: 1, py: .3 }}>
                                        <VerifiedOutlined sx={{ color: B.green, fontSize: 11 }} />
                                        <Typography sx={{ color: B.green, fontSize: '.6rem', fontWeight: 700 }}>LINKED</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* ── STEP 1: Select bank + account number ── */}
                {step === 1 && (
                    <>
                        {/* Bank grid */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Select Your Bank</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                                {BANKS.map(b => (
                                    <Box key={b.value} onClick={() => setBank(b.value)} sx={{
                                        borderRadius: '14px', p: 1.5, cursor: 'pointer',
                                        background: bank === b.value ? alpha(b.color, .1) : alpha(isDark ? '#fff' : B.dark, .03),
                                        border: `1.5px solid ${bank === b.value ? alpha(b.color, .4) : dividerC}`,
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        transition: 'all .18s',
                                    }}>
                                        <Box sx={{ width: 30, height: 30, borderRadius: '9px', background: alpha(b.color, .15), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ color: b.color, fontWeight: 800, fontSize: '.65rem' }}>{b.short}</Typography>
                                        </Box>
                                        <Typography sx={{ color: bank === b.value ? textPri : textMid, fontWeight: bank === b.value ? 700 : 500, fontSize: '.72rem', lineHeight: 1.25 }}>{b.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Account number */}
                        <Box sx={{
                            ...glassCard, p: 2.5, mb: 2,
                            border: acctTouched && acctError
                                ? `1.5px solid ${alpha(B.red, .4)}`
                                : acctTouched && acctValid
                                    ? `1.5px solid ${alpha(B.green, .4)}`
                                    : surfaceBdr,
                        }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 1.5, fontWeight: 600 }}>Account Number</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="tel"
                                    placeholder="0000000000"
                                    maxLength={10}
                                    value={accountNo}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                        setAccountNo(val);
                                    }}
                                    onBlur={() => setAcctTouched(true)}
                                    style={{
                                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                        color: textPri, fontSize: '1.3rem', fontWeight: 700,
                                        fontFamily: "'DM Sans', system-ui, sans-serif",
                                        letterSpacing: '.1em',
                                    }}
                                />
                                {acctTouched && accountNo.length > 0 && (
                                    <Typography sx={{ fontSize: '1rem', flexShrink: 0 }}>
                                        {acctValid ? '✅' : '❌'}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mt: 1.5, height: '1px', background: `linear-gradient(90deg, ${acctTouched && acctError ? B.red : acctTouched && acctValid ? B.green : B.purple}, transparent)` }} />

                            {/* digit counter */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                {acctError ? (
                                    <Typography sx={{ color: B.red, fontSize: '.7rem', fontWeight: 500 }}>{acctError}</Typography>
                                ) : acctTouched && acctValid ? (
                                    <Typography sx={{ color: B.green, fontSize: '.7rem', fontWeight: 500 }}>Valid NUBAN account number ✓</Typography>
                                ) : (
                                    <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>10-digit NUBAN account number</Typography>
                                )}
                                <Typography sx={{ color: acctClean.length === 10 ? B.green : textFaint, fontSize: '.7rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {acctClean.length}/10
                                </Typography>
                            </Box>
                        </Box>

                        <Button fullWidth disabled={!bank || !acctValid}
                                onClick={handleVerify}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    '&:disabled': { opacity: .35, boxShadow: 'none' },
                                    '&:hover': { boxShadow: `0 16px 40px ${alpha(B.purple, .5)}` },
                                }}>
                            {loading ? 'Verifying account…' : 'Verify Account'}
                        </Button>

                        {/* Trust badges */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2.5 }}>
                            {['Bank-Grade Encryption', 'CBN Licensed', 'Read-Only Access'].map(t => (
                                <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                                    <ShieldOutlined sx={{ color: textFaint, fontSize: 11 }} />
                                    <Typography sx={{ color: textFaint, fontSize: '.62rem', fontWeight: 500 }}>{t}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                {/* ── STEP 2: Confirm ── */}
                {step === 2 && !loading && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            {/* Bank tile header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, p: 2, borderRadius: '14px', background: alpha(selectedBank?.color ?? B.purple, .07), border: `1px solid ${alpha(selectedBank?.color ?? B.purple, .18)}` }}>
                                <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: alpha(selectedBank?.color ?? B.purple, .15), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ color: selectedBank?.color ?? B.purple, fontWeight: 800, fontSize: '.85rem' }}>{selectedBank?.short}</Typography>
                                </Box>
                                <Box flex={1}>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem' }}>{selectedBank?.label}</Typography>
                                    <Box display="flex" alignItems="center" gap={1} mt={.25}>
                                        <Typography sx={{ color: textFaint, fontSize: '.75rem', letterSpacing: '.08em', fontVariantNumeric: 'tabular-nums' }}>
                                            {acctClean.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}
                                        </Typography>
                                        <IconButton size="small" onClick={handleCopyAcct} sx={{ p: .3 }}>
                                            {copied
                                                ? <CheckOutlined sx={{ fontSize: 13, color: B.green }} />
                                                : <ContentCopyOutlined sx={{ fontSize: 13, color: textFaint }} />
                                            }
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .4, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 20, px: 1.25, py: .4 }}>
                                    <CheckCircleOutline sx={{ color: B.green, fontSize: 12 }} />
                                    <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700 }}>VERIFIED</Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Account Details</Typography>

                            {[
                                { label: 'Bank',           value: selectedBank?.label ?? '' },
                                { label: 'Account Number', value: acctClean.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3') },
                                { label: 'Account Name',   value: 'JOHN A. OKAFOR' },
                                { label: 'Account Type',   value: 'Savings Account' },
                                { label: 'Access Level',   value: 'Read-Only (Safe)' },
                            ].map((row, i, arr) => (
                                <React.Fragment key={row.label}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.25}>
                                        <Typography sx={{ color: textFaint, fontSize: '.8rem' }}>{row.label}</Typography>
                                        <Typography sx={{ color: row.label === 'Account Name' ? textPri : textMid, fontWeight: row.label === 'Account Name' ? 700 : 600, fontSize: '.82rem' }}>{row.value}</Typography>
                                    </Box>
                                    {i < arr.length - 1 && <Divider sx={{ borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}

                            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: alpha(B.green, .07), border: `1px solid ${alpha(B.green, .18)}`, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <ShieldOutlined sx={{ color: B.green, fontSize: 15, mt: .15, flexShrink: 0 }} />
                                <Typography sx={{ color: alpha(isDark ? '#fff' : B.ink, .55), fontSize: '.72rem', lineHeight: 1.5 }}>
                                    We only request read-only access. We can never move money without your explicit approval.
                                </Typography>
                            </Box>
                        </Box>

                        <Button fullWidth onClick={handleLink} disabled={loading}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    '&:disabled': { opacity: .5, boxShadow: 'none' },
                                }}>
                            {loading ? 'Linking account…' : 'Confirm & Link Account'}
                        </Button>
                    </>
                )}

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default LinkBankPage;